import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReceiptHistory from "@/components/ReceiptHistory";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtTL } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  PackagePlus, Loader2, ClipboardList, History, Truck, ShoppingCart, PackageCheck,
  AlertTriangle, Store, Warehouse, Inbox, Printer, FileText, Upload, X, ScanLine, Hash,
} from "lucide-react";
import { printDispatch } from "@/lib/dispatch";
import Pagination from "@/components/Pagination";
import useScannerWedge from "@/lib/useScannerWedge";
import { deviceFlag } from "@/lib/device";

const OTYPE = {
  internal_order: { label: "İç İkmal", cls: "bg-sky-100 text-sky-700", icon: Store },
  purchase_order: { label: "Satın Alma", cls: "bg-amber-100 text-amber-800", icon: ShoppingCart },
};

const DOC_TYPES = [
  { v: "irsaliye", l: "İrsaliye" },
  { v: "fatura", l: "Fatura" },
  { v: "makbuz", l: "Makbuz" },
  { v: "kendi", l: "Kendi Belgemiz (İç Belge)" },
  { v: "diger", l: "Diğer" },
];

export default function GoodsReceipt() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [opPin, setOpPin] = useState("");   // Kimlik Modeli: ortak girişte mal kabulü yapan personel PIN'i
  const [suppliers, setSuppliers] = useState([]);
  const [pending, setPending] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [active, setActive] = useState(null); // order being received
  const [rows, setRows] = useState({}); // product_id -> {qty, unit_cost, expiry_date}
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [corrNote, setCorrNote] = useState("");
  const [docType, setDocType] = useState("");
  const [docNo, setDocNo] = useState("");
  const [docDetail, setDocDetail] = useState("");
  const [delivName, setDelivName] = useState("");
  const [delivPhone, setDelivPhone] = useState("");
  const [docFiles, setDocFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [gPage, setGPage] = useState(1);
  const [gSize, setGSize] = useState(50);

  const loadPending = () => api.get("/orders/pending-receipts").then((r) => setPending(r.data)).catch(() => {});
  const loadReceipts = () => api.get("/goods-receipts").then((r) => setReceipts(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    loadPending();
    loadReceipts();
  }, []);

  useEffect(() => { setGPage(1); }, [tab, gSize, pending.length]);

  const openReceive = (o) => {
    setActive(o);
    setRows(Object.fromEntries(o.lines.map((l) => [l.product_id, {
      qty: l.qty_shipped || 0, unit_cost: l.unit_cost || 0, expiry_date: "",
    }])));
    setNote(""); setCorrNote(""); setCorrecting(false); setOpPin("");
    setDocType(""); setDocNo(""); setDocDetail(""); setDelivName(""); setDelivPhone(""); setDocFiles([]);
  };
  const setRow = (pid, k, v) => setRows((m) => ({ ...m, [pid]: { ...m[pid], [k]: v } }));
  const isPO = active?.order_type === "purchase_order";

  // El terminali / kablosuz okuyucu: açık siparişte okutulan barkod ilgili satırın miktarını +1 artırır.
  const handleScan = async (code) => {
    if (!active) { toast.info("Barkod okundu — önce bir sipariş açın (Mal Kabul)"); return; }
    try {
      const { data } = await api.get(`/products/barcode/${encodeURIComponent(code)}`);
      const line = active.lines.find((l) => l.product_id === data.id);
      if (!line) { toast.error(`Bu barkod bu siparişte yok: ${data.name}`); return; }
      setRows((m) => {
        const cur = Number(m[data.id]?.qty) || 0;
        const max = isPO ? Infinity : (line.qty_shipped || 0);
        if (cur >= max) { toast.warning(`${data.name}: sevk miktarına ulaşıldı (${max})`); return m; }
        toast.success(`Okundu: ${data.name} (${cur + 1})`);
        return { ...m, [data.id]: { ...m[data.id], qty: cur + 1 } };
      });
    } catch { toast.error(`Barkod bulunamadı: ${code}`); }
  };
  useScannerWedge({ onScan: handleScan, enabled: !!active, beep: deviceFlag("scanBeep", true) });

  const uploadDoc = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDocFiles((arr) => [...arr, data]);
      }
      toast.success("Belge yüklendi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const removeDoc = (url) => setDocFiles((arr) => arr.filter((d) => d.url !== url));

  const genDocNo = async () => {
    try {
      const { data } = await api.get("/logistics/generate-doc-no?type=receipt");
      setDocNo(data.doc_no);
      toast.success(`Belge No üretildi: ${data.doc_no} — fiziksel taslağa yazabilirsiniz`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const submitReceive = async () => {
    if (note.trim().length < 3) return toast.error("Açıklama zorunlu (kısa da olsa yazın: örn. sağlam geldi, 2 koli hasarlı, geç geldi)");
    if (!docType) return toast.error("Belge türü seçin (İrsaliye / Fatura / Makbuz / Diğer)");
    if (docType === "diger" && !docDetail.trim()) return toast.error("Belge açıklaması girin");
    if (docFiles.length === 0) return toast.error("İmzalı belge yüklenmesi zorunludur");
    if (user?.is_shared_login && !opPin) return toast.error("Ortak giriş — mal kabulü yapan personelin kendi PIN'i zorunludur");
    setSaving(true);
    try {
      const payload = {
        note,
        operator_pin: user?.is_shared_login ? opPin : undefined,
        doc_type: docType,
        doc_no: docNo || undefined,
        doc_detail: docType === "diger" ? docDetail : undefined,
        delivered_by_name: delivName || undefined,
        delivered_by_phone: delivPhone || undefined,
        doc_urls: docFiles.map((d) => d.url),
        lines: active.lines.map((l) => ({
          product_id: l.product_id,
          qty_received: Number(rows[l.product_id]?.qty) || 0,
          expiry_date: isPO ? (rows[l.product_id]?.expiry_date || null) : undefined,
        })),
      };
      const { data } = await api.post(`/orders/${active.id}/receive`, payload);
      toast.success(`Mal kabul tamamlandı: ${data.receipt_no}`);
      setActive(null); loadPending(); loadReceipts();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submitCorrection = async () => {
    if (!corrNote.trim()) return toast.error("Düzeltme açıklaması girin");
    setSaving(true);
    try {
      await api.post(`/orders/${active.id}/correction`, { note: corrNote.trim() });
      toast.success("Düzeltme talebi iletildi");
      setActive(null); loadPending();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const TabBtn = ({ id, icon: Icon, label, count }) => (
    <button data-testid={`mk-tab-${id}`} onClick={() => setTab(id)}
      className={`inline-flex items-center gap-1.5 rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${tab === id ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>
      <Icon className="h-4 w-4" /> {label}
      {count > 0 && <span className={`ml-1 rounded-full px-1.5 text-[10px] ${tab === id ? "bg-white/25" : "bg-brand-orange text-white"}`}>{count}</span>}
    </button>
  );

  const pagedPending = pending.slice((gPage - 1) * gSize, gPage * gSize);

  return (
    <div className="space-y-5 fade-in" data-testid="goods-receipt-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <PackagePlus className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Depo / Mağaza</p><h1 className="font-heading text-2xl font-bold text-slate-900">Mal Kabul</h1></div>
        </div>
        <div className="inline-flex rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200">
          <TabBtn id="pending" icon={Inbox} label="Bekleyen Siparişler" count={pending.length} />
          <TabBtn id="history" icon={History} label="Geçmiş" />
        </div>
      </div>

      <p className="text-sm text-slate-500">Mal kabul yalnızca <b>sevk edilmiş sipariş</b> üzerinden yapılır. Siparişsiz giriş yapılamaz.</p>

      {tab === "history" ? (
        <ReceiptHistory suppliers={suppliers} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {pending.length === 0 && (
            <div className="col-span-full rounded-md bg-white p-10 text-center text-sm text-slate-400 ring-1 ring-inset ring-slate-200">
              Bekleyen (sevk edilmiş) sipariş yok. Sipariş oluşturmak için "Satın Alma" veya "Transferler" ekranını kullanın.
            </div>
          )}
          {pagedPending.map((o) => {
            const ot = OTYPE[o.order_type] || OTYPE.internal_order;
            return (
              <div key={o.id} data-testid={`pending-order-${o.id}`} className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono text-xs font-semibold text-slate-700">{o.transfer_no}</p>
                    <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${ot.cls}`}><ot.icon className="h-3 w-3" /> {ot.label}</span>
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700"><Truck className="h-3 w-3" /> Yolda</span>
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  {o.order_type === "purchase_order"
                    ? <p className="flex items-center gap-1"><ShoppingCart className="h-3 w-3 text-amber-500" /> {o.supplier}</p>
                    : <p className="flex items-center gap-1"><Warehouse className="h-3 w-3 text-orange-500" /> {o.from_location_name}</p>}
                  <p className="mt-0.5 flex items-center gap-1"><Store className="h-3 w-3 text-emerald-500" /> {o.to_location_name} · {o.lines.length} kalem</p>
                </div>
                {o.correction_open && <p className="mt-2 flex items-center gap-1 text-[11px] text-red-600"><AlertTriangle className="h-3 w-3" /> Açık düzeltme talebi</p>}
                <div className="mt-3 flex gap-2">
                  <Button data-testid={`pending-receive-btn-${o.id}`} onClick={() => openReceive(o)} className="flex-1 bg-brand-green text-white hover:bg-brand-green-600">
                    <PackageCheck className="mr-1.5 h-4 w-4" /> Mal Kabulü Yap
                  </Button>
                  <Button data-testid={`pending-print-btn-${o.id}`} onClick={() => printDispatch(o)} variant="outline" className="border-slate-300" title="Fiziki sayım föyü yazdır / PDF">
                    <Printer className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
          <div className="col-span-full rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
            <p className="overline mb-2">Son Mal Kabuller</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {receipts.length === 0 && <p className="text-sm text-slate-400">Henüz kayıt yok.</p>}
              {receipts.slice(0, 6).map((r) => (
                <div key={r.id} data-testid={`receipt-row-${r.id}`} className="flex items-center justify-between rounded-sm bg-slate-50 px-3 py-2">
                  <div><p className="font-mono text-[11px] text-slate-700">{r.receipt_no}</p><p className="text-[10px] text-slate-400">{r.supplier} · {r.lines.length} kalem</p></div>
                  <span className="tabular text-sm font-bold text-brand-green">{fmtTL(r.total)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "pending" && pending.length > 0 && (
        <Pagination page={gPage} pageSize={gSize} total={pending.length} onPage={setGPage} onPageSize={setGSize} testid="mk-pagination" />
      )}

      {/* Receive dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="receive-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Mal Kabul — {active.transfer_no}</DialogTitle>
                <DialogDescription>
                  {isPO ? `${active.supplier} → ${active.to_location_name}` : `${active.from_location_name} → ${active.to_location_name}`}
                  {" · "}Gelen ürünü kontrol edip teslim alınan miktarı girin.
                </DialogDescription>
              </DialogHeader>

              {!correcting ? (
                <div className="space-y-3">
                  <div data-testid="mk-scan-hint" className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-[12px] font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">
                    <ScanLine className="h-4 w-4 shrink-0" /> El terminali / okuyucu ile okuttukça ilgili ürünün teslim miktarı otomatik artar.
                  </div>
                  <div className="rounded-md ring-1 ring-inset ring-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-left text-[11px] text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Ürün</th>
                          <th className="px-3 py-2 text-right">Sevk</th>
                          <th className="px-3 py-2 text-right">Teslim</th>
                          {isPO && <th className="px-3 py-2 text-right">SKT</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {active.lines.map((l) => (
                          <tr key={l.product_id}>
                            <td className="px-3 py-2">{l.name}</td>
                            <td className="px-3 py-2 text-right tabular text-slate-400">{l.qty_shipped}</td>
                            <td className="px-3 py-2 text-right">
                              <Input data-testid={`recv-qty-${l.product_id}`} type="number" min="0" max={isPO ? undefined : l.qty_shipped}
                                value={rows[l.product_id]?.qty ?? ""} onChange={(e) => setRow(l.product_id, "qty", e.target.value)} className="h-8 w-16 tabular" />
                            </td>
                            {isPO && (
                              <td className="px-3 py-2 text-right">
                                <DatePickerTR testid={`recv-expiry-${l.product_id}`} value={rows[l.product_id]?.expiry_date ?? ""} onChange={(v) => setRow(l.product_id, "expiry_date", v)} className="mt-0 h-8 w-32" />
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/70 p-3">
                    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><FileText className="h-3.5 w-3.5" /> Belge Bilgisi (zorunlu)</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="mb-0.5 block text-[11px] font-medium text-slate-500">Belge Türü</label>
                        <select data-testid="recv-doc-type" value={docType} onChange={(e) => setDocType(e.target.value)}
                          className="h-9 w-full appearance-none rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                          <option value="">Seçin</option>
                          {DOC_TYPES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="mb-0.5 block text-[11px] font-medium text-slate-500">{docType === "diger" ? "Açıklama" : "Belge No (boş = otomatik)"}</label>
                        {docType === "diger" ? (
                          <Input data-testid="recv-doc-detail" value={docDetail} onChange={(e) => setDocDetail(e.target.value)} placeholder="Belge açıklaması" className="h-9" />
                        ) : (
                          <div className="flex gap-1.5">
                            <Input data-testid="recv-doc-no" value={docNo} onChange={(e) => setDocNo(e.target.value)} placeholder="Örn: İRS-2026-0012 · yoksa Üret" className="h-9" />
                            <Button type="button" data-testid="recv-doc-gen" onClick={genDocNo} variant="outline" className="h-9 shrink-0 whitespace-nowrap px-2 text-xs">
                              <Hash className="mr-1 h-3.5 w-3.5" /> Üret
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="rounded-md bg-white p-2 ring-1 ring-inset ring-slate-200">
                      <p className="mb-1 text-[11px] font-medium text-slate-500">Getiren Kişi <span className="text-slate-400">(sipariş oluşturan otomatik gelir; farklı kişi getirdiyse girin)</span></p>
                      {active.requested_by && <p className="mb-1 text-[11px] text-slate-500">Sipariş oluşturan: <b className="text-slate-700">{active.requested_by}</b></p>}
                      <div className="grid grid-cols-2 gap-2">
                        <Input data-testid="recv-deliv-name" value={delivName} onChange={(e) => setDelivName(e.target.value)} placeholder="Ad Soyad (getiren)" className="h-9" />
                        <Input data-testid="recv-deliv-phone" value={delivPhone} onChange={(e) => setDelivPhone(e.target.value)} placeholder="Telefon" className="h-9" />
                      </div>
                    </div>
                    <div>
                      <p className="mb-1 text-[11px] font-medium text-slate-500">İmzalı Belge <span className="text-red-500">*</span> <span className="font-normal text-slate-400">— mal kabul onayı için zorunludur</span></p>
                      <input id="recv-doc-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" multiple className="hidden" onChange={uploadDoc} />
                      <Button type="button" data-testid="recv-doc-upload" onClick={() => document.getElementById("recv-doc-file").click()} disabled={uploading} variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10">
                        {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} İmzalı Belge Yükle
                      </Button>
                      {docFiles.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {docFiles.map((d) => (
                            <span key={d.url} data-testid="recv-doc-chip" className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-[11px] text-slate-600 ring-1 ring-inset ring-slate-200">
                              <FileText className="h-3 w-3 text-brand-green" /> {d.filename || d.ext}
                              <button onClick={() => removeDoc(d.url)} className="text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <Input data-testid="receive-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Açıklama (zorunlu): örn. sağlam geldi / 2 koli hasarlı / geç geldi" />
                  {user?.is_shared_login && (
                    <div className="rounded-md bg-indigo-50 p-3 ring-1 ring-inset ring-indigo-200">
                      <label className="mb-1 block text-xs font-semibold text-indigo-700">Personel Kodu / PIN <span className="text-red-500">*</span></label>
                      <Input data-testid="receive-operator-pin" type="password" inputMode="numeric" autoComplete="new-password" value={opPin} onChange={(e) => setOpPin(e.target.value.replace(/\D/g, ""))} placeholder="Kendi PIN'iniz ••••" className="tabular tracking-widest" />
                      <p className="mt-1 text-[11px] text-indigo-500">Ortak giriş — mal kabulü <b>yapan personelin kendi PIN'i</b> gerekir. Kayıtta gerçek kişi görünür.</p>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-2">
                      <Button data-testid="receive-print-btn" onClick={() => printDispatch(active)} variant="outline" className="border-slate-300">
                        <Printer className="mr-1.5 h-4 w-4" /> Föy Yazdır / PDF
                      </Button>
                      <Button data-testid="request-correction-btn" onClick={() => setCorrecting(true)} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                        <AlertTriangle className="mr-1.5 h-4 w-4" /> Düzeltme Talep Et
                      </Button>
                    </div>
                    <Button data-testid="confirm-receive-order-btn" onClick={submitReceive} disabled={saving || (user?.is_shared_login && !opPin)} className="bg-brand-green text-white hover:bg-brand-green-600">
                      {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Mal Kabulü Onayla
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">Sevkiyatta hata/eksik varsa açıklayın. Talep, depo sorumlusu ve bölge sorumlusuna iletilir.</p>
                  <Input data-testid="correction-note" value={corrNote} onChange={(e) => setCorrNote(e.target.value)} placeholder="Örn: 2 koli hasarlı geldi, iade gerekiyor" />
                  <div className="flex justify-between gap-2">
                    <Button variant="outline" onClick={() => setCorrecting(false)}>Geri</Button>
                    <Button data-testid="submit-correction-btn" onClick={submitCorrection} disabled={saving} className="bg-brand-orange text-white hover:bg-orange-600">
                      {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Düzeltme Talebini Gönder
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
